import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AccessDeniedError,
  EmptyResponse,
  Guid,
  IMobizHttpService,
  IMobizStateService,
  SerializableObjectList,
} from '../../common-react/core';
import { Inject, Injectable, InjectionToken } from '../../common-react/injection';

import { AccountUsesNewFlow } from '../data/account-uses-new-flow.do';
import { CardVerificationResponse } from '../data/card-verification-response.do';
import { OneTimePaymentResponse } from '../data/one-time-payment-response.do';
import { PaymentCard } from '../data/payment-card.do';
import { UserProfile } from '../data/user-profile.do';
import { ICardPaymentOptions } from '../interfaces/card-payment-options.interface';
import { PosturinnApiEndpointConfiguration } from '../other/api-endpoint-configuration.interface';
import { PosturinnServerApi } from '../other/posturinn-server-api.enum';

export interface IUserProfileService {
  // all available methods
  getUserProfile(): Observable<UserProfile>;
  updateUserProfile(profile: UserProfile): Observable<UserProfile>;

  getPaymentCards(): Observable<PaymentCard[]>;
  addPaymentCard(paymentCard: PaymentCard): Observable<PaymentCard>;
  updatePaymentCard(paymentCard: PaymentCard): Observable<PaymentCard>;
  deletePaymentCard(paymentCardId: Guid): Observable<EmptyResponse>;
  payWithCard(options: ICardPaymentOptions, save: boolean): Observable<CardVerificationResponse>;
  payWithCardChallengeDecidedByIssuer(options: ICardPaymentOptions, save: boolean): Observable<OneTimePaymentResponse>;
  payWithSavedCard(options: ICardPaymentOptions, save: boolean): Observable<EmptyResponse>;

  acceptGdprTerms(): Observable<EmptyResponse>;

  getAccountUsesNewFlow(): Observable<AccountUsesNewFlow>;
}

export const IUserProfileService: InjectionToken<IUserProfileService> = new InjectionToken<IUserProfileService>(
  'IUserProfileService'
);

@Injectable()
export class UserProfileService implements IUserProfileService {
  private get accountId(): string {
    if (this.stateService.currentMembership.value === null) {
      throw new AccessDeniedError('Missing membership data.');
    }
    return this.stateService.currentMembership.value.organizationId;
  }

  constructor(
    @Inject(IMobizHttpService)
    private httpService: IMobizHttpService,
    @Inject(IMobizStateService)
    private stateService: IMobizStateService
  ) {}

  public getUserProfile(): Observable<UserProfile> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.MyProfile,
    };
    return this.httpService.get(new UserProfile(), config);
  }

  public updateUserProfile(userProfile: UserProfile): Observable<UserProfile> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.MyProfile,
      body: JSON.stringify(userProfile),
    };

    return this.httpService.put(new UserProfile(), config);
  }

  public getPaymentCards(): Observable<PaymentCard[]> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ProfilePaymentCards,
      urlParameters: {
        accountId: this.accountId,
      },
    };

    const list: SerializableObjectList<PaymentCard> = new SerializableObjectList(PaymentCard);
    return this.httpService.get(list, config).pipe(
      map((response: SerializableObjectList<PaymentCard>): PaymentCard[] => {
        return response.items ? response.items : [];
      })
    );
  }

  public addPaymentCard(paymentCard: PaymentCard): Observable<PaymentCard> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ProfilePaymentCards,
      urlParameters: {
        accountId: this.accountId,
      },
      disableErrorHandling: true,
      body: JSON.stringify(paymentCard),
    };
    return this.httpService.post(new PaymentCard(), config);
  }

  public updatePaymentCard(paymentCard: PaymentCard): Observable<PaymentCard> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ProfilePaymentCard,
      urlParameters: {
        accountId: this.accountId,
        paymentCardId: paymentCard.id,
      },
      body: JSON.stringify(paymentCard),
    };
    return this.httpService.put(new PaymentCard(), config);
  }

  public deletePaymentCard(paymentCardId: Guid): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.ProfilePaymentCard,
      urlParameters: {
        accountId: this.accountId,
        paymentCardId: paymentCardId,
      },
    };
    return this.httpService.delete(new EmptyResponse(), config);
  }

  public payWithCard(options: ICardPaymentOptions, saveCardInProfile: boolean): Observable<CardVerificationResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.PaymentWithCardVerify,
      urlParameters: {
        accountId: this.accountId,
      },
      queryParameters: {
        save: saveCardInProfile,
      },
      body: JSON.stringify(options),
    };
    return this.httpService.post(new CardVerificationResponse(), config);
  }

  public payWithCardChallengeDecidedByIssuer(options: ICardPaymentOptions, saveCardInProfile: boolean): Observable<OneTimePaymentResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.PayWithCardChallengeDecidedByIssuer,
      urlParameters: {
        accountId: this.accountId,
      },
      queryParameters: {
        save: saveCardInProfile,
      },
      body: JSON.stringify(options),
    };
    return this.httpService.post(new OneTimePaymentResponse(), config);
  }

  public payWithSavedCard(options: ICardPaymentOptions, saveCardInProfile: boolean): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.PaymentWithCard,
      urlParameters: {
        accountId: this.accountId,
      },
      queryParameters: {
        save: saveCardInProfile,
      },
      body: JSON.stringify(options),
    };
    return this.httpService.post(new EmptyResponse(), config);
  }

  public acceptGdprTerms(): Observable<EmptyResponse> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.SetGdprTerms,
    };
    return this.httpService.post(new EmptyResponse(), config);
  }

  public getAccountUsesNewFlow(): Observable<AccountUsesNewFlow> {
    const config: PosturinnApiEndpointConfiguration = {
      serverApi: PosturinnServerApi.AccountUsesNewFlow,
      urlParameters: {
        accountId: this.accountId,
      },
    };

    const one = new AccountUsesNewFlow();
    return this.httpService.get(one, config);
  }
}
